import { Box, Button, Grid, IconButton, Switch, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CustomTitle from 'components/shared/CustomTitle';
import MyTable from 'components/shared/Tables/MyTable/MyTable';
import { encryptUserData, getNetworkCols, validateCredentials } from 'components/utils/networks';
import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { sendToSocket } from '../../../../redux/saga/socket';
import { networkDeviceActions } from '../../../../redux/slice/networkDevice.slice';
import { SAVE_NETWORK_CREDENTIALS } from '../../../../redux/types/networkDevice.types';
// removed large login button styles

const IPTable = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const deviceNetworks = useSelector(networkDeviceActions.selectors.getNetworkDevices);
    const selectedNetwork = deviceNetworks.find(network => network._id === id);

    const [rows, setRows] = useState(
        selectedNetwork.credentials.map(credential => ({
            id: credential.ip,
            ip: credential.ip,
            credentials: { ...credential.credentials }
        }))
    );

    const [credentials, setCredentials] = useState(
        selectedNetwork.credentials.reduce((acc, credential) => {
            acc[credential.ip] = { ...credential.credentials };
            return acc;
        }, {})
    );

    const [validationErrors, setValidationErrors] = useState(
        selectedNetwork.credentials.reduce((acc, credential) => {
            acc[credential.ip] = {
                username: false,
                password: false,
                deviceType: false,
                isVerified: false
            };
            return acc;
        }, {})
    );

    const handleChange = (ip, field, value) => {
        setCredentials(prev => ({
            ...prev,
            [ip]: {
                ...prev[ip],
                [field]: value,
            },
        }));
        setRows(prev => prev.map(r => r.id === ip ? { ...r, credentials: { ...r.credentials, [field]: value } } : r));
    };

    const [isEditMode, setIsEditMode] = useState(false);

    const handleIpChange = (rowId, newIp) => {
        // Keep row id stable; update ip field and align credentials map to row id
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, ip: newIp } : r));
    };

    const addRow = () => {
        // Generate a placeholder IP until user edits it (id must be unique)
        const base = `new-device-${Date.now()}`;
        const newRow = {
            id: base,
            ip: '',
            credentials: { username: '', password: '', deviceType: '', isVerified: false }
        };
        setRows(prev => [newRow, ...prev]);
        setCredentials(prev => ({ ...prev, [base]: { ...newRow.credentials } }));
        setValidationErrors(prev => ({ ...prev, [base]: { username: false, password: false, deviceType: false, isVerified: false } }));
    };

    const removeRow = (rowId) => {
        setRows(prev => prev.filter(r => r.id !== rowId));
        setCredentials(({ [rowId]: _, ...rest }) => rest);
        setValidationErrors(({ [rowId]: __, ...rest }) => rest);
    };

    const handleSubmit = () => {
        if (validateCredentials(credentials, setValidationErrors, validationErrors)) {
            const encryptedCredentials = encryptUserData(credentials);
            // Use current rows to support newly added ones
            const formattedData = rows.map(r => ({
                ip: r.ip || r.id,
                credentials: encryptedCredentials[r.id] || encryptedCredentials[r.ip]
            }));
            const payload = {
                networkId: selectedNetwork.networkId,
                orgId: selectedNetwork.orgId,
                credentials: formattedData
            };
            sendToSocket("NETWORK_CREDENTIALS", payload);
            dispatch({ type: SAVE_NETWORK_CREDENTIALS, payload });
        }
    };

    const rowsWithCredentials = rows.map(r => ({ ...r, credentials: credentials[r.id] || r.credentials }));

    const isAllVerified = useMemo(() => {
        return rowsWithCredentials?.every(item => item?.credentials?.isVerified) ?? false;
    }, [rowsWithCredentials]);


    return (
        <Grid item xs={12}>
            <Grid container alignItems={'center'}>
                <Grid item xs>
                    <CustomTitle title={"Network Credentials"} count={rowsWithCredentials?.length} />
                </Grid>
                <Grid item>
                    <Button onClick={addRow} startIcon={<AddIcon />} variant='outlined'>Create Network Device</Button>
                </Grid>
            </Grid>
            <Box style={{ marginTop: '10px', backgroundColor: "white", borderRadius: "10px" }}>
                <MyTable
                    checkboxSelection={false}
                    columns={[
                        ...getNetworkCols(handleChange, validationErrors, (row) => isEditMode || !row.credentials?.isVerified, handleIpChange),
                        {
                            field: 'actions',
                            headerName: 'Actions',
                            flex: 0.5,
                            renderCell: (params) => (
                                <IconButton aria-label="delete" onClick={() => removeRow(params.row.id)}>
                                    <DeleteIcon />
                                </IconButton>
                            )
                        }
                    ]}
                    rows={rowsWithCredentials}
                />
            </Box>
            <br />
            <Grid container item justifyContent={'flex-end'} xs={12}>
                <Grid item display={'flex'} gap={1} justifyContent={'flex-end'} xs={12}>
                    <Tooltip title="Toggle Edit Mode">
                        <Box display="flex" alignItems="center" mr={2}>
                            <Switch checked={isEditMode} onChange={(e) => setIsEditMode(e.target.checked)} />
                        </Box>
                    </Tooltip>
                    <Button disabled={isAllVerified} onClick={handleSubmit} type="submit" variant='contained' size="small">Submit</Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default IPTable
